/**
 * Airwallex Google Pay Adapter
 *
 * Uses Google Pay API directly (not Stripe.js) to show the payment sheet
 * and extract the encrypted payment token for Airwallex processing.
 *
 * Flow:
 * 1. initialize() - Checks if Google Pay API is available
 * 2. createPaymentDataRequest() - Configures the payment request
 * 3. canMakePayment() - Checks if user can pay with Google Pay
 * 4. showPaymentSheet() - Shows Google Pay sheet and returns encrypted token
 *
 * Mock scenarios are supported for E2E testing without real Google Pay.
 */

export enum AirwallexGooglePayMockScenario {
  None = "none",
  Success = "success",
  Cancelled = "cancelled",
}

// Google Pay API types
// See: https://developers.google.com/pay/api/web/reference/request-objects
declare global {
  interface Window {
    google?: {
      payments: {
        api: {
          PaymentsClient: new (config: GooglePayClientConfig) => GooglePaymentsClient;
        };
      };
    };
  }
}

interface GooglePayClientConfig {
  environment: "TEST" | "PRODUCTION";
}

interface GooglePaymentsClient {
  isReadyToPay(request: IsReadyToPayRequest): Promise<IsReadyToPayResponse>;
  loadPaymentData(request: PaymentDataRequest): Promise<PaymentData>;
}

interface IsReadyToPayRequest {
  apiVersion: number;
  apiVersionMinor: number;
  allowedPaymentMethods: AllowedPaymentMethod[];
}

interface IsReadyToPayResponse {
  result: boolean;
}

interface AllowedPaymentMethod {
  type: "CARD";
  parameters: {
    allowedAuthMethods: ("PAN_ONLY" | "CRYPTOGRAM_3DS")[];
    allowedCardNetworks: ("VISA" | "MASTERCARD" | "AMEX" | "DISCOVER" | "JCB")[];
  };
  tokenizationSpecification?: TokenizationSpecification;
}

interface TokenizationSpecification {
  type: "PAYMENT_GATEWAY";
  parameters: {
    gateway: string;
    gatewayMerchantId: string;
  };
}

interface PaymentDataRequest extends IsReadyToPayRequest {
  merchantInfo: {
    merchantId?: string;
    merchantName: string;
  };
  transactionInfo: {
    totalPriceStatus: "FINAL" | "ESTIMATED";
    totalPrice: string;
    currencyCode: string;
    countryCode: string;
  };
  emailRequired?: boolean;
}

interface PaymentData {
  email?: string;
  paymentMethodData: {
    type: string;
    tokenizationData: {
      type: string;
      token: string; // JSON string containing the encrypted payment token
    };
    info?: {
      cardNetwork: string;
      cardDetails: string;
    };
  };
}

// Google Pay encrypted token structure (what's inside tokenizationData.token)
export interface GooglePayEncryptedToken {
  protocolVersion: string;
  signature: string;
  intermediateSigningKey: {
    signedKey: string;
    signatures: string[];
  };
  signedMessage: string;
}

export interface AirwallexGooglePayConfig {
  merchantId: string; // Business name for display
  gatewayMerchantId: string; // Airwallex account ID (acct_xxxx)
  amountDisplay: string; // Formatted amount for Google Pay (e.g., "100.00" for USD, "1000" for JPY)
  currency: string; // e.g., "usd"
  country: string; // e.g., "US"
  isProduction?: boolean; // Default: false (TEST environment)
  googleMerchantId?: string; // Google-assigned merchant ID for production (BCR2DN...)
}

export type AirwallexShowPaymentResult =
  | { success: true; token: GooglePayEncryptedToken; payerEmail?: string }
  | { success: false; cancelled: true }
  | { success: false; error: string };

export class AirwallexGooglePayAdapter {
  private client: GooglePaymentsClient | null = null;
  private config: AirwallexGooglePayConfig | null = null;
  private mockScenario: AirwallexGooglePayMockScenario;

  constructor(mockScenario?: AirwallexGooglePayMockScenario) {
    this.mockScenario = mockScenario ?? AirwallexGooglePayMockScenario.None;
  }

  /**
   * Initialize the Google Pay client.
   * Returns true if Google Pay API is available.
   */
  initialize(config: AirwallexGooglePayConfig): boolean {
    // Mock scenarios bypass real Google Pay initialization
    if (this.mockScenario !== AirwallexGooglePayMockScenario.None) {
      console.log("[MockGooglePay:Airwallex] initialize called (mock mode)");
      this.config = config;
      return true;
    }

    if (!window.google?.payments?.api?.PaymentsClient) {
      console.error("[GooglePay:Airwallex] Google Pay API not loaded");
      return false;
    }

    this.config = config;
    this.client = new window.google.payments.api.PaymentsClient({
      environment: config.isProduction ? "PRODUCTION" : "TEST",
    });

    return this.client !== null;
  }

  /**
   * Build the allowed payment methods configuration for Google Pay.
   */
  private getAllowedPaymentMethods(includeTokenization: boolean): AllowedPaymentMethod[] {
    if (!this.config) {
      throw new Error("Config not set. Call initialize() first.");
    }

    const baseMethod: AllowedPaymentMethod = {
      type: "CARD",
      parameters: {
        allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
        allowedCardNetworks: ["VISA", "MASTERCARD", "AMEX", "DISCOVER", "JCB"],
      },
    };

    if (includeTokenization) {
      baseMethod.tokenizationSpecification = {
        type: "PAYMENT_GATEWAY",
        parameters: {
          gateway: "airwallex",
          gatewayMerchantId: this.config.gatewayMerchantId,
        },
      };
    }

    return [baseMethod];
  }

  /**
   * Check if the user can make a payment with Google Pay.
   */
  async canMakePayment(): Promise<boolean> {
    // Mock scenarios always return true
    if (this.mockScenario !== AirwallexGooglePayMockScenario.None) {
      console.log("[MockGooglePay:Airwallex] canMakePayment: true (mock mode)");
      return true;
    }

    if (!this.client) {
      return false;
    }

    try {
      const request: IsReadyToPayRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: this.getAllowedPaymentMethods(false),
      };

      const response = await this.client.isReadyToPay(request);
      return response.result;
    } catch (error) {
      console.error("[GooglePay:Airwallex] canMakePayment error:", error);
      return false;
    }
  }

  /**
   * Show the Google Pay payment sheet and return the encrypted token.
   */
  async showPaymentSheet(): Promise<AirwallexShowPaymentResult> {
    // Handle mock scenarios
    if (this.mockScenario === AirwallexGooglePayMockScenario.Success) {
      console.log("[MockGooglePay:Airwallex] showPaymentSheet: returning mock success token");
      // Return a mock token that the backend will recognize as mock
      const mockToken: GooglePayEncryptedToken = {
        protocolVersion: "ECv2",
        signature: "MOCK_SIGNATURE",
        intermediateSigningKey: {
          signedKey: "MOCK_SIGNED_KEY",
          signatures: ["MOCK_SIGNATURE"],
        },
        signedMessage: "MOCK_SIGNED_MESSAGE",
      };
      return { success: true, token: mockToken, payerEmail: "mock@example.com" };
    }

    if (this.mockScenario === AirwallexGooglePayMockScenario.Cancelled) {
      console.log("[MockGooglePay:Airwallex] showPaymentSheet: returning mock cancelled");
      return { success: false, cancelled: true };
    }

    // Real Google Pay flow
    if (!this.client || !this.config) {
      return { success: false, error: "Google Pay not initialized" };
    }

    try {
      const totalPrice = this.config.amountDisplay;

      const request: PaymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: this.getAllowedPaymentMethods(true),
        merchantInfo: {
          merchantName: this.config.merchantId,
          // merchantId is required in PRODUCTION environment
          // It's the Google-assigned merchant ID from Google Pay Business Console
          ...(this.config.isProduction && this.config.googleMerchantId
            ? { merchantId: this.config.googleMerchantId }
            : {}),
        },
        transactionInfo: {
          totalPriceStatus: "FINAL",
          totalPrice: totalPrice,
          currencyCode: this.config.currency.toUpperCase(),
          countryCode: this.config.country.toUpperCase(),
        },
        emailRequired: true,
      };

      const paymentData = await this.client.loadPaymentData(request);

      // Parse the encrypted token from the response
      const tokenString = paymentData.paymentMethodData.tokenizationData.token;
      const token: GooglePayEncryptedToken = JSON.parse(tokenString);

      return { success: true, token, payerEmail: paymentData.email };
    } catch (error) {
      // Google Pay API throws an error when user cancels
      if (error instanceof Error && error.message?.includes("CANCELED")) {
        return { success: false, cancelled: true };
      }

      // Check for statusCode which indicates user cancelled
      const gpayError = error as { statusCode?: string };
      if (gpayError.statusCode === "CANCELED") {
        return { success: false, cancelled: true };
      }

      console.error("[GooglePay:Airwallex] Payment sheet error:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      return { success: false, error: errorMessage };
    }
  }
}
